/**
 * Pure topology helpers: Kahn's topological sort + cycle detection,
 * port-compat check. Used by the BlueprintBuilder for the live
 * topo-order preview and the isValidConnection gate.
 */

import type { Edge, Node } from '@xyflow/react';

import type { PortDefinition } from '@/api/types';

import type { BlueprintNodeData } from './types';

/** Returns the topological order or `null` if a cycle exists. */
export function topologicalOrder(nodes: Node[], edges: Edge[]): string[] | null {
  const in_degree = new Map<string, number>();
  const out_edges = new Map<string, string[]>();
  nodes.forEach((n) => {
    in_degree.set(n.id, 0);
    out_edges.set(n.id, []);
  });
  edges.forEach((e) => {
    in_degree.set(e.target, (in_degree.get(e.target) ?? 0) + 1);
    out_edges.get(e.source)?.push(e.target);
  });
  const ready: string[] = [];
  in_degree.forEach((d, id) => {
    if (d === 0) ready.push(id);
  });
  ready.sort();
  const order: string[] = [];
  while (ready.length > 0) {
    const u = ready.shift()!;
    order.push(u);
    for (const v of out_edges.get(u) ?? []) {
      const nd = (in_degree.get(v) ?? 0) - 1;
      in_degree.set(v, nd);
      if (nd === 0) ready.push(v);
    }
    ready.sort();
  }
  return order.length === nodes.length ? order : null;
}

/** Port-compat result for the inspector / tooltip. */
export interface CompatResult {
  ok: boolean;
  reason?: string;
  source_type?: string;
  target_type?: string;
}

export function portCompat(
  source: Node | undefined,
  sourceHandle: string | null | undefined,
  target: Node | undefined,
  targetHandle: string | null | undefined,
  edges: Edge[],
): CompatResult {
  if (!source || !target) return { ok: false, reason: 'missing endpoint' };
  if (source.id === target.id) return { ok: false, reason: 'self-loop' };
  if (!sourceHandle || !targetHandle) return { ok: false, reason: 'missing port' };

  const srcData = source.data as unknown as BlueprintNodeData | undefined;
  const tgtData = target.data as unknown as BlueprintNodeData | undefined;
  const srcPort = srcData?.outputs.find((p) => p.name === sourceHandle);
  const tgtPort = tgtData?.inputs.find((p) => p.name === targetHandle);
  if (!srcPort || !tgtPort) return { ok: false, reason: 'port not found' };

  const already = edges.some(
    (e) =>
      e.source === source.id &&
      e.sourceHandle === sourceHandle &&
      e.target === target.id &&
      e.targetHandle === targetHandle,
  );
  if (already) return { ok: false, reason: 'edge already exists' };

  const src_type = portTypeLabel(srcPort);
  const tgt_type = portTypeLabel(tgtPort);
  if (src_type !== tgt_type) {
    return {
      ok: false,
      reason: 'type mismatch',
      source_type: src_type,
      target_type: tgt_type,
    };
  }
  return { ok: true, source_type: src_type, target_type: tgt_type };
}

export function portTypeLabel(port: PortDefinition): string {
  return typeof port.data_type === 'string' ? port.data_type : port.data_type.Custom;
}
